// src/modules/clients/pages/ClientProfilePage.tsx
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Typography,
  Grid,
  Paper,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Skeleton,
  Card,
  CardContent,
  Avatar,
  Divider,
  useTheme,
  useMediaQuery,
  Tooltip,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowBack as ArrowBackIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationOnIcon,
  ReceiptLong as ReceiptLongIcon,
  PointOfSale as PosIcon,
  AssignmentReturn as RefundIcon,
  ListAlt as TicketIcon,
  Launch as OpenIcon,
} from "@mui/icons-material";
import api from "../../../services/api";
import { useAuth } from "../../../context/AuthContext";
import { useProtectedRoute } from "../../../hooks/useProtectedRoute";
import DashboardCard from "../../../components/DashboardCard";
import SendWhatsAppButton from "../../../components/SendWhatsAppButton";
import SendEmailButton from "../../../components/SendEmailButton";

type Client = {
  _id: string;
  fullName?: string;
  email?: string;
  phone?: string;
  address?: string;
  loyaltyPoints?: number;
};

type Invoice = {
  _id: string;
  invoiceNumber?: string;
  grandTotal: number;
  paidAmount?: number;                 // sometimes provided
  payments?: { amount?: number }[];    // fallback source
  paymentStatus?: string;
  createdBy?: { fullName?: string };
  createdAt: string | Date;
};

type Sale = {
  _id: string;
  createdAt: string | Date;
  total: number;
  paidAmount?: number;                 // may be missing
  amountPaid?: number;                 // alternative name
  balanceDue?: number;                 // may be missing
  status: string;
  payments?: { amount?: number }[];    // optional list of payments
};

type Refund = {
  _id: string;
  createdAt: string | Date;
  amount: number;
  processedBy?: { fullName?: string };
  reason?: string;
};

type Ticket = {
  _id: string;
  ticketNumber?: string;
  ref?: string;
  jobType?: string;
  status?: string;
  createdBy?: { fullName?: string };
  createdAt: string | Date;
};

export default function ClientProfilePage() {
  useProtectedRoute();
  const { token } = useAuth();
  const { clientId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isSmDown = useMediaQuery(theme.breakpoints.down("sm"));

  const [client, setClient] = useState<Client | null>(null);
  const [stats, setStats] = useState<any>({});
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [sales, setSales] = useState<Sale[]>([]);
  const [refunds, setRefunds] = useState<Refund[]>([]);
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(true);

  const heroGradient = useMemo(
    () =>
      `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 55%, #0f172a 100%)`,
    [theme]
  );
  const surfaceBorder = `1px solid ${theme.palette.mode === "light" ? "#e5e7eb" : theme.palette.divider}`;
  const fmt = (n: number | undefined) =>
    Intl.NumberFormat(undefined, { style: "currency", currency: "USD" }).format(n ?? 0);

  // ------- Helpers -------
  const computeInvoicePaid = useCallback((inv: Invoice) =>
    typeof inv.paidAmount === "number"
      ? inv.paidAmount
      : (inv.payments || []).reduce((sum, p) => sum + (p?.amount || 0), 0),
    []
  );

  const invoiceStatusColor = (status?: string) => {
    const s = (status || "").toUpperCase();
    if (s.includes("PAID") && !s.includes("PART")) return "success";
    if (s.includes("PART")) return "warning"; // PARTIAL / PARTIALLY PAID
    if (s.includes("OVERDUE")) return "error";
    return "error"; // UNPAID or unknown
  };

  const computeSalePaid = useCallback((s: Sale) => {
    if (typeof s.paidAmount === "number" && s.paidAmount > 0) return s.paidAmount;
    if (typeof s.amountPaid === "number" && s.amountPaid > 0) return s.amountPaid;
    if (Array.isArray(s.payments) && s.payments.length) {
      return s.payments.reduce((sum, p) => sum + (p?.amount || 0), 0);
    }
    if (typeof s.balanceDue === "number" && typeof s.total === "number") {
      return Math.max(0, s.total - s.balanceDue);
    }
    if ((s.status || "").toLowerCase() === "paid" && typeof s.total === "number") {
      return s.total;
    }
    return 0;
  }, []);

  const computeSaleOutstanding = useCallback((s: Sale) => {
    if (typeof s.balanceDue === "number") return s.balanceDue;
    if (typeof s.total === "number") return Math.max(0, s.total - computeSalePaid(s));
    return 0;
  }, [computeSalePaid]);

  // Historical totals (Invoices + POS)
  const totals = useMemo(() => {
    let paidInv = 0;
    let outstandingInv = 0;
    for (const inv of invoices) {
      const paid = computeInvoicePaid(inv);
      paidInv += paid;
      outstandingInv += Math.max(inv.grandTotal - paid, 0);
    }
    let paidPOS = 0;
    let outstandingPOS = 0;
    for (const s of sales) {
      paidPOS += computeSalePaid(s);
      outstandingPOS += computeSaleOutstanding(s);
    }
    return {
      paidAllTime: paidInv + paidPOS,
      outstandingAllTime: outstandingInv + outstandingPOS,
      paidInv,
      outstandingInv,
      paidPOS,
      outstandingPOS,
    };
  }, [invoices, sales, computeInvoicePaid, computeSalePaid, computeSaleOutstanding]);

  // ------- Data fetch -------
  useEffect(() => {
    const fetchClientData = async () => {
      try {
        const headers = { Authorization: `Bearer ${token}` };
        const [clientRes, salesRes, refundRes] = await Promise.all([
          api.get(`/clients/${clientId}/profile`, { headers }),
          api.get(`/sales/by-client/${clientId}`, { headers }),
          api.get(`/refunds?clientId=${clientId}`, { headers }),
        ]);

        setClient(clientRes.data.client);
        setStats(clientRes.data.stats);
        setInvoices(clientRes.data.invoices || []);
        setSales(salesRes.data || []);
        setRefunds(refundRes.data || []);
      } catch {
        // handled visually below
      }

      // Tickets with fallback
      try {
        const headers = { Authorization: `Bearer ${token}` };
        const t1 = await api.get(`/tickets/by-client/${clientId}`, { headers });
        setTickets(Array.isArray(t1.data) ? t1.data : t1.data?.tickets || []);
      } catch {
        try {
          const headers = { Authorization: `Bearer ${token}` };
          const t2 = await api.get(`/tickets?clientId=${clientId}`, { headers });
          setTickets(Array.isArray(t2.data) ? t2.data : t2.data?.tickets || []);
        } catch {
          setTickets([]);
        }
      } finally {
        setLoading(false);
      }
    };

    if (clientId && token) fetchClientData();
  }, [clientId, token]);

  // ------- UI -------
  if (loading) {
    return (
      <Box p={{ xs: 2, md: 3 }}>
        <Skeleton height={44} width="32%" />
        <Skeleton variant="rectangular" height={180} sx={{ mt: 2, borderRadius: 2 }} />
        <Skeleton variant="rectangular" height={320} sx={{ mt: 2, borderRadius: 2 }} />
      </Box>
    );
  }

  if (!client) {
    return (
      <Box p={{ xs: 2, md: 3 }}>
        <Typography variant="h6" color="error">
          Client not found.
        </Typography>
        <Button startIcon={<ArrowBackIcon />} sx={{ mt: 2 }} onClick={() => navigate("/clients")}>
          Back to Clients
        </Button>
      </Box>
    );
  }

  return (
    <Box p={{ xs: 2, md: 3 }} component={motion.div} initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      {/* HERO HEADER */}
      <Card
        elevation={0}
        sx={{
          mb: 3,
          borderRadius: 3,
          overflow: "hidden",
          border: surfaceBorder,
          background: heroGradient,
          color: "white",
        }}
      >
        <CardContent sx={{ py: { xs: 2.25, md: 2.75 } }}>
          <Grid container alignItems="center" spacing={2} justifyContent="space-between">
            <Grid item xs={12} md="auto">
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                <Avatar
                  sx={{
                    bgcolor: "rgba(255,255,255,0.15)",
                    border: "1px solid rgba(255,255,255,0.18)",
                    width: 44,
                    height: 44,
                    backdropFilter: "blur(4px)",
                  }}
                >
                  <PersonIcon />
                </Avatar>
                <Box>
                  <Typography variant={isSmDown ? "h5" : "h4"} sx={{ fontWeight: 900, lineHeight: 1.15 }}>
                    {client.fullName || "Client"}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Client Overview &amp; Financial Summary
                  </Typography>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} md>
              <Box
                sx={{
                  width: "100%",
                  display: "flex",
                  gap: 1,
                  justifyContent: { xs: "space-between", md: "flex-end" },
                  mt: { xs: 1.25, md: 0 },
                  flexWrap: "wrap",
                }}
              >
                <Button
                  variant="outlined"
                  color="inherit"
                  startIcon={<ArrowBackIcon />}
                  onClick={() => navigate("/clients")}
                  sx={{ fontWeight: 800, borderColor: "rgba(255,255,255,0.55)", color: "white" }}
                >
                  Back
                </Button>
                <SendWhatsAppButton clientId={client._id} />
                <SendEmailButton clientId={client._id} />
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* CONTACT INFO */}
      <Card elevation={0} sx={{ mb: 3, borderRadius: 3, border: surfaceBorder }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 900, mb: 1.5 }}>
            Contact Info
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <EmailIcon fontSize="small" color="primary" />
                <Typography variant="body2">
                  <strong>Email:</strong> {client.email || "—"}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <PhoneIcon fontSize="small" color="primary" />
                <Typography variant="body2">
                  <strong>Phone:</strong> {client.phone || "—"}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <LocationOnIcon fontSize="small" color="primary" />
                <Typography variant="body2" noWrap title={client.address || ""}>
                  <strong>Address:</strong> {client.address || "—"}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Chip
                label={`${client.loyaltyPoints ?? 0} pts`}
                color={(client.loyaltyPoints ?? 0) > 0 ? "success" : "default"}
                size="small"
                sx={{ fontWeight: 800 }}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* STATS (All-time) */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} sm={6} md={3}>
          <DashboardCard title="Total Jobs" value={stats.totalJobs} color="blue" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <DashboardCard title="Invoices" value={stats.totalInvoices} color="purple" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Tooltip title="Invoices + POS (all time)">
            <Box>
              <DashboardCard title="Paid (All Time)" value={fmt(totals.paidAllTime)} color="green" />
            </Box>
          </Tooltip>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Tooltip title="Invoices + POS (all time)">
            <Box>
              <DashboardCard title="Outstanding (All Time)" value={fmt(totals.outstandingAllTime)} color="red" />
            </Box>
          </Tooltip>
        </Grid>
      </Grid>

      {/* INVOICES */}
      <Card elevation={0} sx={{ mb: 3, borderRadius: 3, border: surfaceBorder, overflow: "hidden" }}>
        <Box sx={{ px: 2, pt: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <ReceiptLongIcon color="primary" />
            <Typography variant="h6" sx={{ fontWeight: 900 }}>
              Invoices
            </Typography>
            <Box sx={{ ml: "auto", display: "flex", gap: 2, color: "text.secondary" }}>
              <Typography variant="body2">Paid: {fmt(totals.paidInv)}</Typography>
              <Typography variant="body2">Outstanding: {fmt(totals.outstandingInv)}</Typography>
            </Box>
          </Box>
        </Box>
        <Divider sx={{ mt: 1 }} />
        <TableContainer component={Paper} elevation={0} sx={{ maxHeight: { md: 420 }, overflow: "auto" }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow
                sx={{
                  "& th": { backgroundColor: theme.palette.mode === "light" ? "#f8fafc" : "action.hover", fontWeight: 800 },
                }}
              >
                <TableCell>Invoice #</TableCell>
                <TableCell>Total</TableCell>
                <TableCell>Paid</TableCell>
                <TableCell>Balance</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Created By</TableCell>
                <TableCell>Date</TableCell>
                <TableCell align="right">Open</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {invoices.map((inv) => {
                const paid = computeInvoicePaid(inv);
                const balance = Math.max(inv.grandTotal - paid, 0);
                return (
                  <TableRow
                    key={inv._id}
                    hover
                    sx={{
                      cursor: "pointer",
                      "&:hover": {
                        background:
                          theme.palette.mode === "light"
                            ? "linear-gradient(0deg, rgba(37,99,235,0.05), rgba(37,99,235,0.05))"
                            : "rgba(37,99,235,0.08)",
                      },
                    }}
                    onClick={() => navigate(`/invoices/${inv._id}`)}
                  >
                    <TableCell sx={{ fontWeight: 700, textDecoration: "underline" }}>
                      {inv.invoiceNumber || "—"}
                    </TableCell>
                    <TableCell>{fmt(inv.grandTotal)}</TableCell>
                    <TableCell>{fmt(paid)}</TableCell>
                    <TableCell>{fmt(balance)}</TableCell>
                    <TableCell>
                      <Chip
                        label={inv.paymentStatus || "UNPAID"}
                        color={invoiceStatusColor(inv.paymentStatus) as any}
                        size="small"
                        sx={{ fontWeight: 700 }}
                      />
                    </TableCell>
                    <TableCell>{inv.createdBy?.fullName || "—"}</TableCell>
                    <TableCell>{new Date(inv.createdAt).toLocaleDateString()}</TableCell>
                    <TableCell align="right">
                      <OpenIcon fontSize="small" />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* TICKETS */}
      <Card elevation={0} sx={{ mb: 3, borderRadius: 3, border: surfaceBorder, overflow: "hidden" }}>
        <Box sx={{ px: 2, pt: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <TicketIcon color="primary" />
            <Typography variant="h6" sx={{ fontWeight: 900 }}>
              Tickets
            </Typography>
          </Box>
        </Box>
        <Divider sx={{ mt: 1 }} />
        <TableContainer component={Paper} elevation={0} sx={{ maxHeight: { md: 380 }, overflow: "auto" }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow
                sx={{
                  "& th": { backgroundColor: theme.palette.mode === "light" ? "#f8fafc" : "action.hover", fontWeight: 800 },
                }}
              >
                <TableCell>Ticket #</TableCell>
                <TableCell>Job Type</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Created By</TableCell>
                <TableCell>Date</TableCell>
                <TableCell align="right">Open</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tickets.map((t) => (
                <TableRow
                  key={t._id}
                  hover
                  sx={{
                    cursor: "pointer",
                    "&:hover": {
                      background:
                        theme.palette.mode === "light"
                          ? "linear-gradient(0deg, rgba(37,99,235,0.05), rgba(37,99,235,0.05))"
                          : "rgba(37,99,235,0.08)",
                    },
                  }}
                  onClick={() => navigate(`/tickets/${t._id}`)}
                >
                  <TableCell sx={{ fontWeight: 700, textDecoration: "underline" }}>
                    {t.ticketNumber || t.ref || t._id.slice(-6).toUpperCase()}
                  </TableCell>
                  <TableCell>{t.jobType || "—"}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={t.status || "—"}
                      color={
                        (t.status || "").toLowerCase() === "completed"
                          ? "success"
                          : (t.status || "").toLowerCase() === "in progress"
                          ? "info"
                          : (t.status || "").toLowerCase() === "pending"
                          ? "warning"
                          : "default"
                      }
                      sx={{ fontWeight: 700 }}
                    />
                  </TableCell>
                  <TableCell>{t.createdBy?.fullName || "—"}</TableCell>
                  <TableCell>{new Date(t.createdAt).toLocaleDateString()}</TableCell>
                  <TableCell align="right">
                    <OpenIcon fontSize="small" />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* POS SALES */}
      <Card elevation={0} sx={{ mb: 3, borderRadius: 3, border: surfaceBorder, overflow: "hidden" }}>
        <Box sx={{ px: 2, pt: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <PosIcon color="primary" />
            <Typography variant="h6" sx={{ fontWeight: 900 }}>
              POS Sales
            </Typography>
            <Box sx={{ ml: "auto", display: "flex", gap: 2, color: "text.secondary" }}>
              <Typography variant="body2">Paid: {fmt(totals.paidPOS)}</Typography>
              <Typography variant="body2">Outstanding: {fmt(totals.outstandingPOS)}</Typography>
            </Box>
          </Box>
        </Box>
        <Divider sx={{ mt: 1 }} />
        <TableContainer component={Paper} elevation={0} sx={{ maxHeight: { md: 360 }, overflow: "auto" }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow
                sx={{
                  "& th": { backgroundColor: theme.palette.mode === "light" ? "#f8fafc" : "action.hover", fontWeight: 800 },
                }}
              >
                <TableCell>Date</TableCell>
                <TableCell>Total</TableCell>
                <TableCell>Paid</TableCell>
                <TableCell>Balance</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sales.map((s) => (
                <TableRow
                  key={s._id}
                  hover
                  sx={{
                    cursor: "pointer",
                    "&:hover": {
                      background:
                        theme.palette.mode === "light"
                          ? "linear-gradient(0deg, rgba(37,99,235,0.05), rgba(37,99,235,0.05))"
                          : "rgba(37,99,235,0.08)",
                    },
                  }}
                  onClick={() => navigate(`/pos/sales/${s._id}`)}
                >
                  <TableCell>{new Date(s.createdAt).toLocaleDateString()}</TableCell>
                  <TableCell>{fmt(s.total)}</TableCell>
                  <TableCell>{fmt(computeSalePaid(s))}</TableCell>
                  <TableCell>{fmt(computeSaleOutstanding(s))}</TableCell>
                  <TableCell>
                    <Chip
                      label={s.status}
                      color={(s.status || "").toUpperCase() === "PAID" ? "success" : "warning"}
                      size="small"
                      sx={{ fontWeight: 700 }}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* REFUNDS */}
      <Card elevation={0} sx={{ mb: 3, borderRadius: 3, border: surfaceBorder, overflow: "hidden" }}>
        <Box sx={{ px: 2, pt: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <RefundIcon color="primary" />
            <Typography variant="h6" sx={{ fontWeight: 900 }}>
              Refunds
            </Typography>
          </Box>
        </Box>
        <Divider sx={{ mt: 1 }} />
        <TableContainer component={Paper} elevation={0} sx={{ maxHeight: { md: 320 }, overflow: "auto" }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow
                sx={{
                  "& th": { backgroundColor: theme.palette.mode === "light" ? "#f8fafc" : "action.hover", fontWeight: 800 },
                }}
              >
                <TableCell>Date</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Processed By</TableCell>
                <TableCell>Reason</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {refunds.map((r) => (
                <TableRow
                  key={r._id}
                  hover
                  sx={{
                    "&:hover": {
                      background:
                        theme.palette.mode === "light"
                          ? "linear-gradient(0deg, rgba(37,99,235,0.05), rgba(37,99,235,0.05))"
                          : "rgba(37,99,235,0.08)",
                    },
                  }}
                >
                  <TableCell>{new Date(r.createdAt).toLocaleDateString()}</TableCell>
                  <TableCell>{fmt(r.amount)}</TableCell>
                  <TableCell>{r.processedBy?.fullName || "—"}</TableCell>
                  <TableCell>{r.reason || "—"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Actions (mobile-friendly) */}
      <Box sx={{ display: { xs: "flex", md: "none" }, gap: 1, mb: 1 }}>
        <SendWhatsAppButton clientId={client._id} />
        <SendEmailButton clientId={client._id} />
      </Box>

      {/* Space before fixed footer seam */}
      <Box sx={{ height: 12 }} />
    </Box>
  );
}



